import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect, useMemo } from "react";
import DBService from "../../services/DBService";
import { debugLogTime } from "../../utils/common";
import ClioPageLoader from "../../services/loaders/ClioPageLoader";

const DataLoadingContext = createContext(undefined);

// Action types
export const ACTION_TYPES = {
  START_POLLING_TABLE: "START_POLLING_TABLE",
  START_POLLING_ALL: "START_POLLING_ALL",
  UPDATE_TABLE_STATE: "UPDATE_TABLE_STATE",
  STOP_POLLING_TABLE: "STOP_POLLING_TABLE",
  RELOAD_TABLE: "RELOAD_TABLE",
  UPDATE_TABLE: "UPDATE_TABLE",
  CONTINUE_LOADING_TABLE: "CONTINUE_LOADING_TABLE",
};

// Initial state for a table
const createInitialTableState = () => ({
  lastRequest: "initial",
  loaded: 0,
  percentComplete: 0,
  records: 0,
  status: "Not Started",
  errorMessage: null,
  errorStatus: null,
  totalLoadingTime: "N/A",
  updated: 0,
  version: 0,
  isPolling: false,
});

// Reducer
const dataLoadingReducer = (state, action) => {
  debugLogTime("dataLoadingReducer", { state, action }, "DataLoadingContext");

  switch (action.type) {
    case ACTION_TYPES.START_POLLING_TABLE: {
      const { tableName } = action.payload;
      return {
        ...state,
        [tableName]: {
          ...(state[tableName] || createInitialTableState()),
          isPolling: true,
        },
      };
    }

    case ACTION_TYPES.START_POLLING_ALL: {
      const { tableNames } = action.payload;
      const updates = {};
      tableNames.forEach((tableName) => {
        updates[tableName] = {
          ...(state[tableName] || createInitialTableState()),
          isPolling: true,
        };
      });
      return {
        ...state,
        ...updates,
      };
    }

    case ACTION_TYPES.UPDATE_TABLE_STATE: {
      const { tableName, data } = action.payload;
      return {
        ...state,
        [tableName]: {
          ...(state[tableName] || createInitialTableState()),
          ...data,
          isPolling: data.status === "complete" ? false : state[tableName]?.isPolling || false,
          errorMessage: data.errorMessage || state[tableName]?.errorMessage || null,
          errorStatus: data.errorStatus || state[tableName]?.errorStatus || null,
        },
      };
    }

    case ACTION_TYPES.STOP_POLLING_TABLE: {
      const { tableName } = action.payload;
      return {
        ...state,
        [tableName]: {
          ...(state[tableName] || createInitialTableState()),
          isPolling: false,
          errorMessage: state[tableName]?.errorMessage || null,
          errorStatus: state[tableName]?.errorStatus || null,
        },
      };
    }

    case ACTION_TYPES.RELOAD_TABLE: {
      const { tableName, doneCallback } = action.payload;
      // trigger a table reload in CLioPageLoader
      ClioPageLoader.loadTables([tableName], doneCallback, () => {});
      console.log("Triggered reload for table: ", tableName);
      return {
        ...state,
        [tableName]: {
          ...(state[tableName] || createInitialTableState()),
          // Reset relevant fields for reload
          lastRequest: "reload_requested",
          loaded: 0,
          percentComplete: 0,
          status: "Not Started",
        },
      };
    }

    case ACTION_TYPES.CONTINUE_LOADING_TABLE: {
      const { tableName } = action.payload;
      // trigger a table continue loading in CLioPageLoader
      ClioPageLoader.continueLoadingTables([tableName]);
      console.log("Triggered continue loading for table: ", tableName);
      return {
        ...state,
        [tableName]: {
          ...(state[tableName] || createInitialTableState()),
          lastRequest: "continue_loading_requested",
          status: "loading",
        },
      };
    }

    case ACTION_TYPES.UPDATE_TABLE: {
      const { tableName, doneCallback } = action.payload;
      // trigger a table update in CLioPageLoader
      ClioPageLoader.updateTables([tableName], doneCallback);
      console.log("Triggered update for table: ", tableName);
      return {
        ...state,
        [tableName]: {
          ...(state[tableName] || createInitialTableState()),
          lastRequest: "update_requested",
          loaded: 0,
          percentComplete: 0,
          status: "Not Started",
        },
      };
    }

    default:
      return state;
  }
};

/**
 * DataLoadingProvider - Provides polling functionality for table loading status
 *
 * @param {Object} props
 * @param {string[]} props.tableNames - Array of table names to monitor
 * @param {number} props.pollingInterval - Polling interval in milliseconds (default: 1000)
 */
const DataLoadingProvider = ({ children, tableNames = [], pollingInterval = 1000 }) => {
  const [state, dispatch] = useReducer(dataLoadingReducer, {});
  const intervalRefs = useRef({});

  // Poll a single table
  const pollTable = useCallback(async (tableName) => {
    try {
      debugLogTime("pollTable", tableName, "DataLoadingContext");
      const data = await DBService.getData(`${tableName}_updated`);

      // getData returns an array, we need the first element
      const tableData = data && data.length > 0 ? data[0] : null;

      if (tableData) {
        dispatch({
          type: ACTION_TYPES.UPDATE_TABLE_STATE,
          payload: {
            tableName,
            data: {
              lastRequest: tableData.lastRequest || "initial",
              loaded: tableData.loaded || 0,
              percentComplete: tableData.percentComplete || 0,
              records: tableData.records || 0,
              status: tableData.status || "Not Started",
              errorStatus: tableData.errorStatus || null,
              errorMessage: tableData.errorMessage || null,
              totalLoadingTime: tableData.totalLoadingTime || "N/A",
              updated: tableData.updated || 0,
              version: tableData.version || 0,
            },
          },
        });

        // Stop polling if complete
        if (tableData.status === "complete") {
          debugLogTime("stopPollingTable", { tableName }, "DataLoadingContext");
          if (intervalRefs.current[tableName]) {
            clearInterval(intervalRefs.current[tableName]);
            delete intervalRefs.current[tableName];
          }
        }
      }
    } catch (error) {
      // can happen when we start polling BEFORE there are any records in the IndexedDB
      debugLogTime("pollTable error", { tableName, error }, "DataLoadingContext");
    }
  }, []);

  // Start polling a single table
  const startPollingTable = useCallback(
    (tableName) => {
      debugLogTime("startPollingTable", tableName, "DataLoadingContext");

      // Stop existing polling if any
      if (intervalRefs.current[tableName]) {
        clearInterval(intervalRefs.current[tableName]);
      }

      // Dispatch action to update state
      dispatch({
        type: ACTION_TYPES.START_POLLING_TABLE,
        payload: { tableName },
      });

      // Start polling immediately
      pollTable(tableName);

      // Set up interval for continued polling
      intervalRefs.current[tableName] = setInterval(() => {
        pollTable(tableName);
      }, pollingInterval);
    },
    [pollTable, pollingInterval],
  );

  // Stop polling a single table
  const stopPollingTable = useCallback(
    (tableName) => {
      debugLogTime("stopPollingTable", tableName, "DataLoadingContext");

      if (intervalRefs.current[tableName]) {
        clearInterval(intervalRefs.current[tableName]);
        delete intervalRefs.current[tableName];
      }

      dispatch({
        type: ACTION_TYPES.STOP_POLLING_TABLE,
        payload: { tableName },
      });
    },
    [dispatch],
  );

  // Start polling all tables
  const startPollingAll = useCallback(() => {
    debugLogTime("startPollingAll", tableNames, "DataLoadingContext");

    // Dispatch action to update state
    dispatch({
      type: ACTION_TYPES.START_POLLING_ALL,
      payload: { tableNames },
    });

    // Start polling each table
    tableNames.forEach((tableName) => {
      startPollingTable(tableName);
    });
  }, [tableNames, startPollingTable]);

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      Object.keys(intervalRefs.current).forEach((tableName) => {
        clearInterval(intervalRefs.current[tableName]);
      });
      intervalRefs.current = {};
    };
  }, []);

  const value = useMemo(
    () => ({
      state,
      dispatch,
      startPollingTable,
      startPollingAll,
      stopPollingTable,
    }),
    [state, dispatch, startPollingTable, startPollingAll, stopPollingTable],
  );

  return <DataLoadingContext.Provider value={value}>{children}</DataLoadingContext.Provider>;
};

/**
 * Hook to use DataLoadingContext
 */
function useDataLoading() {
  const context = useContext(DataLoadingContext);
  if (context === undefined) {
    throw new Error("useDataLoading must be used within a DataLoadingProvider");
  }
  return context;
}

export { DataLoadingProvider, useDataLoading };
